import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ITour } from "@interfaces";
import { breakpoint, theme, useWindowDimensions } from "@utilities";
import { TourElementModule } from "@module";
import { PATH_FAVORITES } from "@routes";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { useEffect, useState } from "react";
import { ClipLoaderCommon } from "@components";

interface IProps {
    data?: ITour[];
    type?: string | null;
}

export const PromoTourModule = (props: IProps) => {
    const { data = [], type } = props;
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const isMobile = width < 768;

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        breakpoints: {
            "(min-width: 1024px)": {
                slides: {
                    perView: 4,
                    spacing: 22,
                },
            },
            "(min-width: 1200px)": {
                slides: {
                    perView: 4,
                    spacing: 22,
                },
            },
            "(min-width: 768px)": {
                slides: {
                    perView: 4,
                    spacing: 22,
                },
            },
            "(max-width: 475px)": {
                slides: {
                    perView: 1.5,
                },
            },
        },
    });

    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        setIsReady(false);
        const timer = setTimeout(() => setIsReady(true), 100);
        return () => clearTimeout(timer);
    }, [data]);

    useEffect(() => {
        if (isReady && instanceRef.current) {
            instanceRef.current.update();
        }
    }, [isReady]);

    const handlePrev = () => {
        instanceRef.current?.prev();
    };

    const handleNext = () => {
        instanceRef.current?.next();
    };

    const handleSeeAll = () => {
        if (type === "favorite") {
            navigate(PATH_FAVORITES);
        }
    };

    if (isMobile) {
        return (
            <MobileWrapper>
                <MobileGrid>
                    {data.map((tour, index) => (
                        <MobileGridItem key={`${tour.productId}-${index}`}>
                            <TourElementModule data={tour} />
                        </MobileGridItem>
                    ))}
                </MobileGrid>
            </MobileWrapper>
        );
    }

    return (
        <Wrapper>
            {data.length > 1 && (
                <button className="prevBtn" onClick={handlePrev}>
                    <img src="/images/website/destinations/fi_chevron-left.png" alt="prev" />
                </button>
            )}
            <div className="sliderWrapper">
                {type && data.length > 3 && (
                    <button className="seeAll" onClick={handleSeeAll}>
                        See all
                    </button>
                )}
                {isReady ? (
                    <div ref={sliderRef} className="keen-slider">
                        {data.map((tour, index) => (
                            <div className="keen-slider__slide" key={`${tour.productId}-${index}`}>
                                <div className="tour-card-wrapper">
                                    <TourElementModule data={tour} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
                )}
            </div>
            {data.length > 1 && (
                <button className="nextBtn" onClick={handleNext}>
                    <img src="/images/website/destinations/fi_chevron-right.png" alt="next" />
                </button>
            )}
        </Wrapper>
    );
};

const MobileWrapper = styled.div`
    width: 100%;
    overflow: hidden;
    margin-bottom: 8px;
`;

const MobileGrid = styled.div`
    display: grid;
    grid-template-rows: repeat(2, 1fr);
    grid-auto-flow: column;
    grid-auto-columns: 65%;
    gap: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    margin: 0 12px 8px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        display: none;
    }
    scrollbar-width: none;
`;

const MobileGridItem = styled.div`
    scroll-snap-align: start;
    min-width: 0;
    padding: 0;

    > div {
        padding: 0;
    }

    .description,
    .supplier {
        display: none;
    }

    .destination__title {
        min-height: unset !important;
        -webkit-line-clamp: 1 !important;
        margin-bottom: 4px !important;
        font-size: 14px !important;
    }

    .destination__place .placeName {
        font-size: 12px !important;
    }

    .tour_image {
        padding-top: 55% !important;
    }

    .main {
        min-height: unset !important;
    }

    .durationNPrice {
        padding-top: 4px;
    }
`;

const Wrapper = styled.div`
    margin: 0 auto;
    position: relative;
    z-index: 0;

    button {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    ${breakpoint.breakMobileMedium`
        width: ${(props) => props.theme.widths.wide_700};
    `}

    ${breakpoint.breakTablet`
        width: ${(props) => props.theme.widths.wide_700};   
    `}

    ${breakpoint.breakIpadPro`
        width: ${(props) => props.theme.widths.wide_850}; 
    `}

    ${breakpoint.breakLarge`
        width: ${(props) => props.theme.widths.wide_1110};
    `}

    .seeAll {
        position: absolute;
        right: 0;
        bottom: calc(100% + 36px);
        font-size: 16px;
        font-weight: 700;
        line-height: 26px;
        color: ${(p) => p.theme.colors.main};
        z-index: 5;
        background: transparent;
        border: none;
        cursor: pointer;
    }

    .sliderWrapper {
        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
    }

    .keen-slider__slide {
        min-width: 0;
        display: flex;
        justify-content: center;
        width: 260px;
        max-width: 260px;
        margin: 0;

        ${breakpoint.breakMobile`
        height: 280px;
    `}
    }

    .tour-card-wrapper {
        width: 100%;
        height: 100%;

        ${breakpoint.breakMobile`
            height: 240px;
        `}
    }

    .prevBtn {
        display: flex;
        alignitems: center;
        justifycontent: center;
        position: absolute;
        border-radius: 50%;
        top: 40%;
        left: -5%;
        height: 32px;
        width: 32px;
        ${breakpoint.breakMobile`
            background: #fff;
            display: none;
        `}
        background: transparent;
        border: none;
        cursor: pointer;
        z-index: 10;
    }

    .nextBtn {
        display: flex;
        alignitems: center;
        justifycontent: center;
        position: absolute;
        border-radius: 50%;
        top: 40%;
        right: -5%;
        height: 32px;
        width: 32px;
        ${breakpoint.breakMobile`
            background: #fff;
            display: none;
        `}
        background: transparent;
        border: none;
        cursor: pointer;
        z-index: 10;
    }
`;

export default PromoTourModule;
